// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Size } from '../shared'

/**
 * The `<qds-progress-bar>` element displays an indicator showing the
 * completion progress of a task.
 *
 * @see https://quartz.se.com/build/components/progress-bar
 */
@Component({
  tag: 'qds-progress-bar',
  formAssociated: true,
  shadow: true,
  styleUrl: 'progress-bar.css',
})
export class ProgressBar implements ComponentInterface {
  /**
   * The progress bar's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Returns a list of the [`<label>`][] elements associated with the
   * `qds-progress-bar` element.
   *
   * [`<label>`]: https://developer.mozilla.org/docs/Web/HTML/Element/label
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly labels: ElementInternals['labels'] =
    {} as ElementInternals['labels']

  /**
   * Specifies how much work the task indicated by the progress bar requires.
   * `max` must have a value greater than 0 and be a valid floating point
   * number.
   *
   * @webnative
   */
  @Prop() public readonly max?: number | string

  /**
   * Returns the result of dividing the current value (`value`) by the maximum
   * value (`max`); if the progress bar is an indeterminate progress bar, it
   * returns `-1`.
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly position: number = -1

  /**
   * Specifies how much of the task has been completed. It must be a valid
   * floating point number between `0` and `max`, or between `0` and `1` if
   * `max` is omitted. If this attribute is not specified, the progress bar is
   * indeterminate; this indicates that an activity is ongoing with no
   * indication of how long it is expected to take.
   *
   * @webnative
   */
  @Prop() public readonly value?: number | string

  @Element() private readonly host!: HTMLElement

  @AttachInternals() private readonly internals!: ElementInternals

  @State() private width = '0%'

  #localMax = 1

  #localValue = 0

  #position = -1

  get #computedSize(): Size {
    switch (this.size) {
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  @Watch('max')
  protected maxChanged(): void {
    const max =
      typeof this.max === 'string' ? Number.parseFloat(this.max) : this.max
    this.#localMax = max === undefined || Number.isNaN(max) || max < 0 ? 1 : max

    this.internals.ariaValueMax = this.#localMax.toString()

    this.#positionChanged()
  }

  @Watch('value')
  protected valueChanged(): void {
    const value =
      typeof this.value === 'string'
        ? Number.parseFloat(this.value)
        : this.value
    this.#localValue =
      value === undefined || Number.isNaN(value) || value < 0 ? 0 : value

    this.internals.ariaValueNow =
      // eslint-disable-next-line unicorn/no-null
      this.value === undefined ? null : this.#localValue.toString()

    this.#positionChanged()
  }

  public componentWillLoad(): void {
    this.internals.role = 'progressbar'
    this.#defineGetter('labels', () => this.internals.labels)
    this.#defineGetter('position', () => this.#position)

    this.maxChanged()
    this.valueChanged()
  }

  public render() {
    return (
      <div aria-hidden="true" class="qds-track" data-size={this.#computedSize}>
        <div
          class={{
            'qds-bar': true,
            'qds-indeterminate': this.#position === -1,
          }}
          data-size={this.#computedSize}
          style={{ width: this.width }}
        />
      </div>
    )
  }

  #positionChanged(): void {
    if (this.value === undefined) {
      this.#position = -1
      this.width = '50%'
      return
    }

    this.#position = this.#localValue / this.#localMax
    this.width = `${Math.min(this.#position * 100, 100)}%`
  }

  #defineGetter(p: PropertyKey, get: () => unknown): void {
    Object.defineProperty(this.host, p, { enumerable: true, get })
  }
}
